import { useEffect, useState } from "react";
import { Button } from "@/ui/button";
import { Modal } from "@/ui/modals";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import FilterRow from "./filter-row";
import { MinMaxInput } from "@/ui/min-max-input";
import FilterToggleButton from "@/ui/filter-toggle-button";
import {
  OrderFilters,
  useOrdersFilter,
} from "@/providers/orders-filter/orders-filter";

const ORDERS_TYPES = ["buy", "sell"];

const OrdersFiltersModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { filters, setFilters, clearFilters, defaultFilters } =
    useOrdersFilter();

  const [formData, setFormData] = useState(filters);

  useEffect(() => {
    if (isOpen) {
      setFormData(filters);
    }
  }, [isOpen, filters]);

  const updateFormData = <K extends keyof OrderFilters>(
    key: K,
    value: OrderFilters[K] | ""
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value !== "" ? value : undefined,
    }));
  };

  const handleDone = () => {
    setFilters(formData);
    setIsOpen(false);
  };

  const handleClear = () => {
    clearFilters();
    setFormData(defaultFilters);
    setIsOpen(false);
  };

  return (
    <>
      <GradientBorderRadius radius="bigger" hover="50" className="h-full">
        <button
          onClick={() => setIsOpen(true)}
          className="w-[6.25rem] h-[1.375rem] text-center text-[0.75rem] font-semibold flex items-center justify-center"
        >
          Filters
        </button>
      </GradientBorderRadius>

      <Modal
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        className="w-[28.125rem] py-[0.9375rem] px-[0.6250rem] bg-section-gradient"
      >
        <Modal.Title title="Filters" className="text-xs font-semibold" />
        <div className="w-full h-full grid grid-cols-[max-content,1fr] gap-y-[0.375rem] gap-x-[0.625rem] text-white text-xs font-semibold">
          <FilterRow title="Type" className="h-[1.375rem]">
            <div className="h-full grid grid-cols-2 gap-[0.3125rem]">
              {ORDERS_TYPES.map((t) => (
                <FilterToggleButton
                  key={t}
                  title={t}
                  selected={formData.type === t}
                  className="capitalize"
                  onClick={() =>
                    updateFormData("type", t as OrderFilters["type"])
                  }
                />
              ))}
            </div>
          </FilterRow>
          <FilterRow title="Price">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formData.priceMin}
              maxValue={formData.priceMax}
              onMinChange={(val) => updateFormData("priceMin", val)}
              onMaxChange={(val) => updateFormData("priceMax", val)}
            />
          </FilterRow>
          <FilterRow title="Value">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formData.valueMin}
              maxValue={formData.valueMax}
              onMinChange={(val) => updateFormData("valueMin", val)}
              onMaxChange={(val) => updateFormData("valueMax", val)}
            />
          </FilterRow>
          <FilterRow title="Time">
            <MinMaxInput
              unitSide="right"
              text="h"
              layout="row"
              minValue={formData.timeMin}
              maxValue={formData.timeMax}
              onMinChange={(val) => updateFormData("timeMin", val)}
              onMaxChange={(val) => updateFormData("timeMax", val)}
            />
          </FilterRow>
        </div>
        <Modal.Footer>
          <Button
            title="Done"
            active={false}
            className="w-[9.4375rem] font-semibold text-xs"
            onClick={handleDone}
          />
          <Button
            title="Clear"
            active={false}
            className="w-[9.4375rem] font-semibold text-xs"
            onClick={handleClear}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrdersFiltersModal;
